import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import toast from 'react-hot-toast';
import { Plus, Edit, X } from 'lucide-react';
import axios from 'axios';
import useCustomFields from '../hooks/useCustomFields';
import CustomFieldCell from '../components/custom-fields/CustomFieldCell';
import DataTable from '../components/common/DataTable';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const Vendors = () => {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  const { fields: listCustomFields } = useCustomFields('vendor', { listViewOnly: true });

  const loadVendors = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/vendor-master/vendors`);
      setVendors(response.data || []);
      setFilteredVendors(response.data || []);
    } catch (error) {
      console.error('Error loading vendors:', error);
      toast.error('Failed to load vendors');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVendors();
  }, [loadVendors]);

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('All');
    setFilteredVendors(vendors);
  };

  useEffect(() => {
    let filtered = [...vendors];
    if (searchTerm) {
      filtered = filtered.filter(vendor =>
        vendor.vendor_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vendor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vendor.city.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (statusFilter !== 'All') {
      filtered = filtered.filter(vendor => vendor.status === statusFilter);
    }
    setFilteredVendors(filtered);
  }, [searchTerm, statusFilter, vendors]);

  const columns = [
    { key: 'vendor_number', header: 'Vendor Number', filterable: true, accessor: (v) => v.vendor_number,
      render: (v) => <span className="font-mono text-sm">{v.vendor_number}</span> },
    { key: 'name', header: 'Name', filterable: true, accessor: (v) => v.name,
      render: (v) => <span className="text-sm font-medium">{v.name}</span> },
    { key: 'account_group_id', header: 'Account Group', accessor: (v) => v.account_group_id },
    { key: 'city', header: 'City', filterable: true, accessor: (v) => v.city },
    { key: 'country_key', header: 'Country', accessor: (v) => v.country_key },
    { key: 'status', header: 'Status',
      render: (v) => (
        <span className={`px-2 py-1 text-xs font-medium rounded ${v.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
          {v.status}
        </span>
      ) },
    ...listCustomFields.map((f) => ({
      key: `cf_${f.fieldname}`, header: f.label,
      render: (v) => (
        <span data-testid={`vendor-cf-${f.fieldname}-${v.vendor_number}`}>
          <CustomFieldCell field={f} value={v.custom_data?.[f.fieldname]} />
        </span>
      ),
    })),
    { key: 'actions', header: 'Actions', align: 'right',
      render: (v) => (
        <Button size="sm" variant="outline" onClick={() => navigate(`/vendors/${v.vendor_number}`)}>
          <Edit className="w-4 h-4 mr-1" />Edit
        </Button>
      ) },
  ];

  return (
    <Layout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">View Vendors</h1>
            <p className="text-sm text-gray-500 mt-1">Search and manage vendor master data</p>
          </div>
          {canWrite('vendor_master') && (
          <Button onClick={() => navigate('/vendors/create')}>
            <Plus className="w-4 h-4 mr-2" />
            Create New Vendor
          </Button>
          )}
        </div>

        {/* Search and Filters */}
        <Card className="p-4 mb-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[250px]">
              <Label>Search</Label>
              <Input
                placeholder="Search by vendor number, name, or city..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="mt-1"
              />
            </div>
            <div className="w-40">
              <Label>Status</Label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="All">All</option>
                <option value="Draft">Draft</option>
                <option value="Active">Active</option>
              </select>
            </div>
            <Button variant="outline" onClick={handleClearFilters}>
              <X className="w-4 h-4 mr-2" />
              Clear
            </Button>
          </div>
        </Card>

        <Card className="p-6">
          <DataTable
            testid="vendors-table"
            columns={columns}
            data={filteredVendors}
            loading={isLoading}
            rowKey={(v) => v.vendor_number}
            emptyText={vendors.length === 0 ? 'No vendors found. Click "Create New Vendor" to get started.' : 'No vendors match your search criteria.'}
            rowProps={(v) => ({ 'data-testid': `vendor-${v.vendor_number}` })}
          />
        </Card>
      </div>
    </Layout>
  );
};

export default Vendors;
